// Resonant Moments — emotionally precise detection.
// Scans a completed rehearsal session for moments that feel recognizable,
// validating, and emotionally exact. Not virality optimization.

export interface RehearsalMessage {
  turn: number;
  role: "agent" | "user";
  content: string;
  tactic_tag: string | null;
}

export type MomentKind =
  | "manipulation_phrase"      // a sharp, recognizable manipulation line
  | "invalidation"             // dismissal of feelings/reality
  | "escalation"               // pressure intensification
  | "boundary_breakthrough"    // user holds line clearly
  | "psychological_reversal"   // DARVO / blame inversion
  | "mixed_signal";            // ambiguity weaponized

export interface ResonantMoment {
  kind: MomentKind;
  label: string;        // forensic header, e.g. "Invalidation Pattern"
  quote: string;        // the exact phrase
  context: string;      // tactic_tag or role context
  observation: string;  // calm forensic interpretation
  weight: number;       // internal ranking (not shown)
  turn: number;
}

// Pattern library — calm, precise. Order matters: more specific first.
const PATTERNS: { kind: MomentKind; label: string; observation: string; rx: RegExp; weight: number }[] = [
  {
    kind: "invalidation",
    label: "Invalidation Pattern",
    observation: "A common phrase used to dismiss the other person's experience.",
    rx: /\b(you're (too )?sensitive|you're overreacting|you always overreact|you're imagining|that didn't happen|you're remembering it wrong)\b/i,
    weight: 9,
  },
  {
    kind: "psychological_reversal",
    label: "Reversal of Blame",
    observation: "Blame is redirected toward the person raising the concern. Common in conflict reversals.",
    rx: /\b(this is your fault|you made me|if you hadn't|i wouldn't have to if|look what you did)\b/i,
    weight: 9,
  },
  {
    kind: "manipulation_phrase",
    label: "Guilt Induction",
    observation: "Frames the listener's needs as harm to the speaker.",
    rx: /\b(after everything i've done|i guess i'm just|nobody appreciates|i sacrifice|you don't care about me)\b/i,
    weight: 8,
  },
  {
    kind: "mixed_signal",
    label: "Mixed Signal",
    observation: "Sincerity and dismissal delivered in one sentence. Designed to disorient.",
    rx: /\b(i was just joking|can't you take a joke|i didn't mean it like that|why are you making this a big deal)\b/i,
    weight: 7,
  },
  {
    kind: "escalation",
    label: "Escalation Pressure",
    observation: "Urgency or absolutes used to compress the listener's response space.",
    rx: /\b(always|never|every time|you never|every single time|forget it|whatever|fine then)\b/i,
    weight: 6,
  },
  {
    kind: "manipulation_phrase",
    label: "Conditional Affection",
    observation: "Connection presented as contingent on compliance.",
    rx: /\b(if you (really )?loved me|if you cared|a real (friend|partner) would|i thought you were different)\b/i,
    weight: 8,
  },
];

// Boundary breakthroughs from the user — short, grounded, non-defensive.
const BOUNDARY_RX = /^(no\.|that doesn't work for me|i'm not (going to|available|able to)|i've already (said|told you)|i won't|this isn't up for discussion)/i;

const wc = (s: string) => s.trim().split(/\s+/).filter(Boolean).length;

export function detectResonantMoments(
  messages: RehearsalMessage[],
  maxMoments = 3
): ResonantMoment[] {
  const moments: ResonantMoment[] = [];

  for (const msg of messages) {
    const text = msg.content.trim();
    if (!text) continue;

    if (msg.role === "agent") {
      for (const p of PATTERNS) {
        if (p.rx.test(text)) {
          moments.push({
            kind: p.kind,
            label: p.label,
            quote: text,
            context: msg.tactic_tag || "Manipulation Tactic",
            observation: p.observation,
            weight: p.weight + (text.length < 80 ? 1 : 0), // tighter quotes resonate more
            turn: msg.turn,
          });
          break; // one classification per message
        }
      }
    } else if (msg.role === "user") {
      if (BOUNDARY_RX.test(text) && wc(text) <= 14) {
        moments.push({
          kind: "boundary_breakthrough",
          label: "Boundary Held",
          quote: text,
          context: "Grey Rock Response",
          observation: "Concise, non-defensive language. Removes the surface area for escalation.",
          weight: 7,
          turn: msg.turn,
        });
      }
    }
  }

  // Dedupe by quote, keep highest weight, return top N
  const byQuote = new Map<string, ResonantMoment>();
  for (const m of moments) {
    const key = m.quote.toLowerCase().slice(0, 80);
    const existing = byQuote.get(key);
    if (!existing || m.weight > existing.weight) byQuote.set(key, m);
  }

  return Array.from(byQuote.values())
    .sort((a, b) => b.weight - a.weight)
    .slice(0, maxMoments);
}
